// ReferralRazorPay.jsx - Fixed Frontend Component with Enhanced Error Handling
import React, { useEffect, useState } from "react";
import { auth, db } from "../firebase";
import { doc, updateDoc, setDoc, getDoc } from "firebase/firestore";

const ReferralRazorPay = ({ referralCode, amount, onSuccess, onError = () => {} }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  
  // Configuration
  const SERVER_URL = process.env.REACT_APP_SERVER_URL || "http://localhost:5000";
  const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID;

  console.log("🔧 Configuration:", {
    serverUrl: SERVER_URL,
    razorpayKey: RAZORPAY_KEY_ID ? `${RAZORPAY_KEY_ID.substring(0, 8)}...` : "Missing",
    referralCode,
    amount
  });

  // Load Razorpay script dynamically
  useEffect(() => {
    const loadRazorpay = () => {
      return new Promise((resolve) => {
        if (window.Razorpay) {
          console.log("✅ Razorpay already loaded");
          setRazorpayLoaded(true);
          resolve(true);
          return;
        }

        console.log("📥 Loading Razorpay script...");
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.async = true;
        script.onload = () => {
          console.log("✅ Razorpay script loaded successfully");
          setRazorpayLoaded(true);
          resolve(true);
        };
        script.onerror = () => {
          console.error("❌ Failed to load Razorpay script");
          setRazorpayLoaded(false);
          resolve(false);
        };
        document.body.appendChild(script);
      });
    };

    loadRazorpay();
  }, []);

  // Enhanced validation function
  const validateInputs = () => {
    const errors = [];

    if (!RAZORPAY_KEY_ID) {
      errors.push("Payment service not configured (missing RAZORPAY_KEY_ID)");
    }

    // if (!referralCode || typeof referralCode !== "string" || referralCode.trim().length === 0) {
    //   errors.push("Referral code is required");
    // }

    if (!amount || isNaN(amount) || amount <= 0) {
      errors.push("Invalid amount");
    }

    const user = auth.currentUser;
    if (!user) {
      errors.push("Please login to continue");
    }

    if (!razorpayLoaded || !window.Razorpay) {
      errors.push("Payment service not loaded. Please refresh and try again.");
    }

    return errors;
  };

  // Create order on backend with MUCH better error handling
  const createOrder = async (orderAmount) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("User not authenticated");
      }

      const orderData = {
        amount: parseFloat(orderAmount),
        // referralCode: referralCode.trim(),
        userId: user.uid
      };
      if (referralCode && typeof referralCode === "string" && referralCode.trim().length > 0) {
        orderData.referralCode = referralCode.trim();
      }

      console.log("📤 Creating order with data:", orderData);

      const response = await fetch(`${SERVER_URL}/api/razorpay/create-order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(orderData)
      });

      console.log("📨 Server response status:", response.status);
      console.log("📨 Server response headers:", Object.fromEntries(response.headers.entries()));

      // Always try to get response text first, then parse as JSON
      const responseText = await response.text();
      console.log("📨 Raw server response:", responseText);

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error("❌ Failed to parse server response as JSON:", parseError);
        throw new Error(`Server returned invalid JSON. Status: ${response.status}, Response: ${responseText.substring(0, 200)}`);
      }

      console.log("📨 Parsed server response data:", data);

      if (!response.ok) {
        // Enhanced error message construction
        let errorMessage = data.error || data.message || `HTTP ${response.status}`;
        if (data.details) errorMessage += `: ${data.details}`;
        if (data.code) errorMessage += ` (${data.code})`;
        if (data.missing) errorMessage += ` - Missing: ${data.missing.join(", ")}`;
        
        console.error("❌ Server returned error:", {
          status: response.status,
          error: errorMessage,
          fullResponse: data
        });
        
        throw new Error(errorMessage);
      }

      if (!data.success && data.success !== undefined) {
        let errorMessage = data.error || "Order creation failed";
        if (data.details) errorMessage += `: ${data.details}`;
        throw new Error(errorMessage);
      }

      console.log("✅ Order created successfully:", data);
      return data;
      
    } catch (error) {
      console.error("❌ Error creating order:", error);
      
      // Handle different types of errors with more specific messages
      if (error.name === "TypeError" && error.message.includes("fetch")) {
        throw new Error(`Cannot connect to payment server at ${SERVER_URL}. Please ensure the server is running and accessible.`);
      }
      
      if (error.name === "SyntaxError" || error.message.includes("JSON")) {
        throw new Error("Server returned invalid response. This might be a server configuration issue.");
      }
      
      // Re-throw the original error if it's already descriptive
      throw error;
    }
  };

  // Test server connection (useful for debugging)
  const testServerConnection = async () => {
    try {
      console.log("🔍 Testing server connection...");
      const response = await fetch(`${SERVER_URL}/health`);
      const data = await response.json();
      console.log("✅ Server health check:", data);
      return true;
    } catch (error) {
      console.error("❌ Server connection test failed:", error);
      return false;
    }
  };

  // Enhanced payment handler
  const displayRazorpay = async () => {
    if (isLoading) {
      console.log("⏳ Payment already in progress, ignoring click");
      return;
    }

    console.log("🚀 Starting payment process...");
    setIsLoading(true);

    try {
      // Test server connection first
      const serverReachable = await testServerConnection();
      if (!serverReachable) {
        throw new Error(`Cannot reach payment server at ${SERVER_URL}. Please check if the server is running.`);
      }

      // Validate all inputs
      const validationErrors = validateInputs();
      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(", "));
      }

      const user = auth.currentUser;
      console.log("👤 Current user:", {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName
      });

      // Create order on server
      const orderData = await createOrder(amount);
      
      console.log("🎯 Initializing Razorpay checkout...");

      const options = {
        key: RAZORPAY_KEY_ID,
        amount: orderData.amount,
        currency: orderData.currency || "INR",
        name: "Tax Sarthi",
        description: "Referral Program Activation",
        image: "/logo192.png", // Add your logo here
        order_id: orderData.id,
        handler: function (response) {
          console.log("💰 Payment completed:", response);
          handlePaymentSuccess(response);
        },
        prefill: {
          name: user.displayName || "Customer",
          email: user.email || "",
          contact: user.phoneNumber || "9999999999"
        },
        theme: {
          color: "#3399cc",
        },
        modal: {
          ondismiss: function() {
            console.log("❌ Payment modal dismissed by user");
            setIsLoading(false);
            onError("Payment cancelled by user");
          }
        },
        notes: {
          referralCode: referralCode,
          userId: user.uid,
          purpose: "referral_activation"
        },
        retry: {
          enabled: true,
          max_count: 2
        },
        timeout: 300, // 5 minutes timeout
        remember_customer: false
      };

      console.log("⚙️ Razorpay options:", {
        ...options,
        key: `${options.key.substring(0, 8)}...`,
        amount: `${options.amount} paise (₹${amount})`
      });

      const rzp = new window.Razorpay(options);
      
      rzp.on("payment.failed", function (response) {
        console.error("💸 Payment failed:", response.error);
        setIsLoading(false);
        const errorMsg = response.error.description || response.error.reason || "Payment failed";
        onError(`Payment failed: ${errorMsg}`);
      });

      rzp.on("payment.error", function (response) {
        console.error("💸 Payment error:", response);
        setIsLoading(false);
        onError("Payment error occurred. Please try again.");
      });

      rzp.open();
      
    } catch (error) {
      console.error("❌ Error initiating payment:", error);
      setIsLoading(false);
      onError(error.message || "Failed to initiate payment");
    }
  };

  // Enhanced payment success handler
  const handlePaymentSuccess = async (paymentResponse) => {
    console.log("🔄 Processing payment success...");
    
    try {
      console.log("🔍 Verifying payment with server...", {
        order_id: paymentResponse.razorpay_order_id,
        payment_id: paymentResponse.razorpay_payment_id
      });

      // Verify payment on server
      const verificationResponse = await fetch(`${SERVER_URL}/api/razorpay/verify-payment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({
          razorpay_order_id: paymentResponse.razorpay_order_id,
          razorpay_payment_id: paymentResponse.razorpay_payment_id,
          razorpay_signature: paymentResponse.razorpay_signature
        })
      });

      const verificationData = await verificationResponse.json();
      console.log("🔍 Verification result:", verificationData);
      
      if (!verificationResponse.ok) {
        throw new Error(`Verification failed: ${verificationData.error || "Server error"}`);
      }

      if (verificationData.verified) {
        console.log("✅ Payment verified successfully");
        await updateUserReferralStatus(paymentResponse);
      } else {
        throw new Error("Payment verification failed - signature mismatch");
      }
    } catch (error) {
      console.error("❌ Error processing payment:", error);
      setIsLoading(false);
      onError(`Payment verification failed: ${error.message}`);
    }
  };

  // Separate function for updating user status
  const updateUserReferralStatus = async (paymentResponse) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("User session expired");
      }

      console.log("🔄 Updating user referral status...");

      const email = user.email;
      const match = email.match(/\+(.*?)@/);
      const pan = match ? match[1].toUpperCase() : "";

      if (!pan) {
        throw new Error("Could not extract PAN from email format. Expected format: user+PAN@domain.com");
      }

      console.log("🆔 Extracted PAN:", pan);

      // Update user document in Firebase
      const userDocRef = doc(db, "usersTable", pan);
      const userDocSnap = await getDoc(userDocRef);

      const updateData = {
        referralActivated: true,
        referralActivationDate: new Date().toISOString(),
        razorpayPaymentId: paymentResponse.razorpay_payment_id,
        lastUpdated: new Date().toISOString()
      };

      if (userDocSnap.exists()) {
        console.log("📄 Updating existing user document");
        await updateDoc(userDocRef, updateData);
      } else {
        console.log("📄 Creating new user document");
        await setDoc(userDocRef, {
          ...updateData,
          userId: user.uid,
          email: user.email,
          createdAt: new Date().toISOString()
        });
      }

      // Create detailed payment record
      const paymentRecord = {
        userId: pan,
        userUid: user.uid,
        userEmail: user.email,
        razorpayOrderId: paymentResponse.razorpay_order_id,
        razorpayPaymentId: paymentResponse.razorpay_payment_id,
        razorpaySignature: paymentResponse.razorpay_signature,
        amount: amount * 100, // Store amount in paise
        amountInRupees: amount,
        status: "completed",
        timestamp: new Date().toISOString(),
        verified: true,
        paymentMethod: paymentResponse.razorpay_payment_method || "unknown",
        currency: "INR"
      };

      // Only add referralCode to the record if it exists
      if (referralCode && typeof referralCode === "string" && referralCode.trim().length > 0) {
        paymentRecord.referralCode = referralCode.trim();
      }

      console.log("💾 Creating payment record:", paymentRecord);

      await setDoc(doc(db, "referralPayments", paymentResponse.razorpay_payment_id), paymentRecord);

      console.log("🎉 All updates completed successfully");

      setIsLoading(false);
      onSuccess({
        success: true,
        message: "Payment completed successfully! Your referral program is now active.",
        paymentId: paymentResponse.razorpay_payment_id,
        amount: amount,
        pan: pan
      });

    } catch (error) {
      console.error("❌ Error updating user status:", error);
      setIsLoading(false);
      onError(`Failed to update user status: ${error.message}`);
    }
  };

  return (
    <div className="razorpay-payment">
      <button 
        className={`btn btn-primary ${isLoading ? "loading" : ""}`}
        onClick={displayRazorpay}
        disabled={isLoading || !razorpayLoaded}
        style={{
          minWidth: "200px",
          padding: "12px 24px",
          fontSize: "16px",
          fontWeight: "600",
          borderRadius: "8px",
          border: "none",
          backgroundColor: isLoading || !razorpayLoaded ? "#ccc" : "#3399cc",
          color: "white",
          cursor: isLoading || !razorpayLoaded ? "not-allowed" : "pointer",
          transition: "all 0.3s ease"
        }}
      >
        {isLoading ? (
          <>
            <span 
              className="spinner-border spinner-border-sm me-2" 
              role="status" 
              aria-hidden="true"
              style={{
                width: "16px",
                height: "16px",
                borderWidth: "2px"
              }}
            ></span>
            Processing...
          </>
        ) : (
          `Pay ₹${amount} to Activate Referral`
        )}
      </button>
      
      {!razorpayLoaded && (
        <div className="mt-2">
          <small className="text-muted">
            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
            Loading payment service...
          </small>
        </div>
      )}

      {/* Debug info in development */}
      {process.env.NODE_ENV === "development" && (
        <div className="mt-2">
          <small className="text-muted">
            Server: {SERVER_URL} | 
            Key: {RAZORPAY_KEY_ID ? "✅" : "❌"} | 
            Razorpay: {razorpayLoaded ? "✅" : "❌"}
          </small>
        </div>
      )}
      
      {/* Add server test button in development */}
      {process.env.NODE_ENV === "development" && (
        <div className="mt-2">
          <button 
            className="btn btn-sm btn-outline-secondary"
            onClick={testServerConnection}
            disabled={isLoading}
          >
            Test Server Connection
          </button>
        </div>
      )}
    </div>
  );
};

export default ReferralRazorPay;